/** Session-bound billing transport; providers never receive our account id. */
import { array, bool, object, text, whole } from "./checked_json";
import { API_BASE } from "./api_base";
import { adoptSessionExpiry, readSession } from "./backend";
import type { PlanId } from "./entitlement";
import type { NativePlatform } from "@/native/bridge";
import type { Receipt } from "@/native/purchases";
export type Interval = "monthly" | "yearly";
export interface Offering {
  plan_id: PlanId;
  prices_sgd_cents: Record<Interval, number>;
  ai: { documents: number; messages: number } | null;
  products: Partial<Record<Interval, { product_id: string; base_plan_id?: string }>>;
}
export interface Offerings { customer_ref: string; trial: { available: boolean; days: number; allowance: { documents: number; messages: number } }; plans: Offering[] }
export async function billingRequest(path: string, body?: unknown): Promise<unknown> {
  const session = readSession();
  if (!session) throw new Error("Sign in before choosing a plan.");
  const response = await fetch(`${API_BASE}/v1/${path}`, {
    method: body === undefined ? "GET" : "POST",
    headers: { authorization: `Bearer ${session.token}`, "content-type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  adoptSessionExpiry(response);
  const data: unknown = await response.json();
  const error = object(data).error;
  const errorCode = typeof error === "string" ? error : String(response.status);
  if (!response.ok) throw new BillingRequestError(response.status, errorCode, billingErrorMessage(errorCode));
  return data;
}

/**
 * What a person reads when a billing call fails. The server's code stays on the
 * error (`BillingRequestError.code`) for whoever is diagnosing it; it is never
 * shown, because `temporarily_unavailable` is not a sentence, and as one long
 * unbreakable word it also overflowed the paywall at 320px with large text.
 */
export function billingErrorMessage(code: string): string {
  switch (code) {
    case "subscription_belongs_to_another_account":
      return "This purchase belongs to another account. Contact support.";
    case "already_subscribed":
      return "Your purchase was received, but this account already has a plan. Check Duplicates in Settings › Plan, or contact support.";
    case "temporarily_unavailable":
      return "Plans can't be reached right now. Try again in a moment.";
    default:
      return "Could not update your plan. Try again, and contact support if it keeps happening.";
  }
}

export class BillingRequestError extends Error {
  readonly status: number;
  /** The server's error code, or the HTTP status when it sent none. */
  readonly code: string;
  constructor(status: number, code: string, message: string) { super(message); this.status = status; this.code = code; }
}
function allowance(value: unknown): { documents: number; messages: number } {
  const row = object(value); return { documents: whole(row.documents), messages: whole(row.messages) };
}
export function parseOfferings(value: unknown): Offerings {
  const row = object(value), trial = object(row.trial);
  return { customer_ref: text(row.customer_ref), trial: { available: bool(trial.available), days: whole(trial.days), allowance: allowance(trial.allowance) },
    plans: array(row.plans, (value) => {
      const plan = object(value), prices = object(plan.prices_sgd_cents), products = object(plan.products);
      if (plan.plan_id !== "tally" && plan.plan_id !== "tally_ai" && plan.plan_id !== "tally_ai_plus" && plan.plan_id !== "family") throw new Error("Unknown offering plan.");
      const parsed: Offering["products"] = {};
      for (const interval of ["monthly", "yearly"] as const) if (products[interval] !== undefined) {
        const product = object(products[interval]);
        parsed[interval] = { product_id: text(product.product_id), ...(product.base_plan_id === undefined ? {} : { base_plan_id: text(product.base_plan_id) }) };
      }
      return { plan_id: plan.plan_id, prices_sgd_cents: { monthly: whole(prices.monthly), yearly: whole(prices.yearly) }, products: parsed, ai: plan.ai === null ? null : allowance(plan.ai) };
    }) };
}
export const loadOfferings = async (platform: NativePlatform): Promise<Offerings> => parseOfferings(await billingRequest(`billing/offerings?platform=${platform}`));
export function parseCheckout(value: unknown): { transaction_id: string } { return { transaction_id: text(object(value).transaction_id) }; }
export async function claimPurchase(receipt: Receipt): Promise<void> {
  await billingRequest("billing/claim", receipt.provider === "apple"
    ? { provider: "apple", signed_transaction: receipt.signed_transaction }
    : { provider: "google", purchase_token: receipt.purchase_token, product_id: receipt.product_id });
}

const CHECK_ATTEMPTS = 15;
const CHECK_DELAY_MS = 1_000;
/** A completed Paddle checkout can precede its server webhook by a few seconds. */
export async function confirmWebPurchase(): Promise<void> {
  const { refreshEntitlement } = await import("./entitlement");
  for (let attempt = 0; attempt < CHECK_ATTEMPTS; attempt++) {
    const value = await refreshEntitlement();
    if (value && value.entitlement.state !== "none") return;
    await new Promise<void>((resolve) => window.setTimeout(resolve, CHECK_DELAY_MS));
  }
  throw new Error("Your plan is still being verified. Use Restore purchases to check again.");
}
